import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import { NAV, landingPath, canManager } from "@/lib/nav";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ManagerConsole from "@/pages/ManagerConsole";
import PosApp from "@/pos/PosApp";
import CustomerDisplay from "@/pos/CustomerDisplay";
import StaffPortal from "@/staff/StaffPortal";
import KDS from "@/pages/KDS";
import Orders from "@/pages/Orders";
import Menu from "@/pages/Menu";
import Inventory from "@/pages/Inventory";
import Operations from "@/pages/Operations";
import Procurement from "@/pages/Procurement";
import Suppliers from "@/pages/Suppliers";
import Customers from "@/pages/Customers";
import Locations from "@/pages/Locations";
import Tills from "@/pages/Tills";
import ApprovalInbox from "@/pages/ApprovalInbox";
import Reports from "@/pages/Reports";
import OwnerDigest from "@/pages/OwnerDigest";
import Administration from "@/pages/Administration";
import Settings from "@/pages/Settings";

import AttendanceKiosk from "@/pages/AttendanceKiosk";
import HRLayout from "@/pages/hr/HRLayout";
import HRDashboard from "@/pages/hr/HRDashboard";
import HREmployees from "@/pages/hr/Employees";
import HRDepartments from "@/pages/hr/Departments";
import HRAttendance from "@/pages/hr/Attendance";
import HRShifts from "@/pages/hr/ShiftScheduling";
import HRShiftPlanner from "@/pages/hr/ShiftPlanner";
import HRLeaves from "@/pages/hr/Leaves";
import HRPayroll from "@/pages/hr/Payroll";
import HRPayslips from "@/pages/hr/Payslips";
import HRPayrollRules from "@/pages/hr/PayrollRules";
import HRRecurring from "@/pages/hr/RecurringDeductions";
import HRQueries from "@/pages/hr/Queries";
import HREscalations from "@/pages/hr/Escalations";
import HRReports from "@/pages/hr/HRReports";
import HRSettings from "@/pages/hr/HRSettings";
import HRIam from "@/pages/hr/IAM";

import ManagementLayout from "@/pages/management/ManagementLayout";
import ManagementDashboard from "@/pages/management/ManagementDashboard";
import AlertsCenter from "@/pages/management/AlertsCenter";
import TasksChecklists from "@/pages/management/TasksChecklists";
import Escalations from "@/pages/management/Escalations";
import MgmtMaintenance from "@/pages/management/Maintenance";
import MgmtComplaints from "@/pages/management/Complaints";
import ManagementReports from "@/pages/management/ManagementReports";
import ManagementIntelligence from "@/pages/management/ManagementIntelligence";
import ManagementSettings from "@/pages/management/ManagementSettings";

import AccountingLayout from "@/pages/accounting/AccountingLayout";
import AccountingDashboard from "@/pages/accounting/AccountingDashboard";
import CRMLayout from "@/pages/crm/CRMLayout";
import CRMDashboard from "@/pages/crm/CRMDashboard";
import CRMCustomers from "@/pages/crm/CRMCustomers";
import CustomerProfile from "@/pages/crm/CustomerProfile";
import CRMDuplicates from "@/pages/crm/Duplicates";
import CRMSegments from "@/pages/crm/Segments";
import CRMLoyalty from "@/pages/crm/Loyalty";
import CRMCampaigns from "@/pages/crm/Campaigns";
import CRMJourneys from "@/pages/crm/Journeys";
import CRMActivity from "@/pages/crm/CustomerActivity";
import CRMReports from "@/pages/crm/CRMReports";
import CRMSettings from "@/pages/crm/CRMSettings";
import GeneralLedger from "@/pages/accounting/GeneralLedger";
import Journals from "@/pages/accounting/Journals";
import ChartOfAccounts from "@/pages/accounting/ChartOfAccounts";
import AccountsPayable from "@/pages/accounting/AccountsPayable";
import AccountsReceivable from "@/pages/accounting/AccountsReceivable";
import BankCash from "@/pages/accounting/BankCash";
import Reconciliation from "@/pages/accounting/Reconciliation";
import ReportsHub from "@/pages/accounting/ReportsHub";
import StatementsHub from "@/pages/accounting/StatementsHub";
import TaxVAT from "@/pages/accounting/TaxVAT";
import BudgetActual from "@/pages/accounting/BudgetActual";
import AccReports from "@/pages/accounting/Reports";
import AccountingSettings from "@/pages/accounting/AccountingSettings";
import DailyBranchAccounting from "@/pages/accounting/DailyBranchAccounting";
import BranchComparison from "@/pages/accounting/BranchComparison";
import SettlementReconciliation from "@/pages/accounting/SettlementReconciliation";

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#8B1E1E" }}>
      <div className="flex flex-col items-center gap-4">
        <div className="w-14 h-14 rounded-xl flex items-center justify-center font-brand font-bold text-3xl" style={{ background: "#C49A4A", color: "#3B0D0D" }}>M</div>
        <Loader2 className="animate-spin" style={{ color: "#E7D0AD" }} />
      </div>
    </div>
  );
}

function Protected({ children, bare = false }) {
  const { user, loading, can } = useAuth();
  const location = useLocation();
  if (loading || user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  const isAccounting = location.pathname.startsWith("/accounting");
  const isHR = location.pathname.startsWith("/hr");
  const isMgmt = location.pathname.startsWith("/management");
  const item = NAV.find((n) => n.path === location.pathname);
  const allowed = user.is_admin
    || (isAccounting ? can("accounting", "view")
      : isHR ? can("hr", "view")
      : isMgmt ? can("administration", "view")
      : (item?.key === "manager" ? canManager(user) : (!item || can(item.module || item.key, "view"))));
  if (!allowed) {
    const first = NAV.find((n) => can(n.key, "view"));
    return <Navigate to={first ? first.path : "/login"} replace />;
  }
  return bare ? children : <AppShell>{children}</AppShell>;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Splash />;
  if (user) return <Navigate to={landingPath(user)} replace />;
  return <Login />;
}

const ROUTES = [
  ["/", Dashboard], ["/manager", ManagerConsole], ["/kds", KDS, true], ["/orders", Orders],
  ["/menu", Menu], ["/inventory", Inventory], ["/operations", Operations], ["/procurement", Procurement],
  ["/suppliers", Suppliers], ["/customers", Customers],
  ["/locations", Locations], ["/tills", Tills], ["/inbox", ApprovalInbox], ["/reports", Reports], ["/digest", OwnerDigest], ["/administration", Administration], ["/settings", Settings],
];

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/pos" element={<PosApp />} />
            <Route path="/pos/customer-display" element={<CustomerDisplay />} />
            <Route path="/staff" element={<StaffPortal />} />
            <Route path="/staff/*" element={<StaffPortal />} />
            <Route path="/attendance" element={<AttendanceKiosk />} />
            <Route path="/hr" element={<Protected bare><HRLayout /></Protected>}>
              <Route index element={<HRDashboard />} />
              <Route path="employees" element={<HREmployees />} />
              <Route path="departments" element={<HRDepartments />} />
              <Route path="attendance" element={<HRAttendance />} />
              <Route path="shifts" element={<HRShifts />} />
              <Route path="shift-planner" element={<HRShiftPlanner />} />
              <Route path="leaves" element={<HRLeaves />} />
              <Route path="payroll" element={<HRPayroll />} />
              <Route path="payslips" element={<HRPayslips />} />
              <Route path="payroll-rules" element={<HRPayrollRules />} />
              <Route path="recurring" element={<HRRecurring />} />
              <Route path="queries" element={<HRQueries />} />
              <Route path="escalations" element={<HREscalations />} />
              <Route path="iam" element={<HRIam />} />
              <Route path="reports" element={<HRReports />} />
              <Route path="settings" element={<HRSettings />} />
            </Route>
            <Route path="/crm" element={<Protected bare><CRMLayout /></Protected>}>
              <Route index element={<CRMDashboard />} />
              <Route path="customers" element={<CRMCustomers />} />
              <Route path="customers/:cid" element={<CustomerProfile />} />
              <Route path="duplicates" element={<CRMDuplicates />} />
              <Route path="segments" element={<CRMSegments />} />
              <Route path="loyalty" element={<CRMLoyalty />} />
              <Route path="campaigns" element={<CRMCampaigns />} />
              <Route path="journeys" element={<CRMJourneys />} />
              <Route path="activity" element={<CRMActivity />} />
              <Route path="reports" element={<CRMReports />} />
              <Route path="settings" element={<CRMSettings />} />
            </Route>
            <Route path="/management" element={<Protected bare><ManagementLayout /></Protected>}>
              <Route index element={<ManagementDashboard />} />
              <Route path="alerts" element={<AlertsCenter />} />
              <Route path="tasks" element={<TasksChecklists />} />
              <Route path="escalations" element={<Escalations />} />
              <Route path="maintenance" element={<MgmtMaintenance />} />
              <Route path="complaints" element={<MgmtComplaints />} />
              <Route path="reports" element={<ManagementReports />} />
              <Route path="intelligence" element={<ManagementIntelligence />} />
              <Route path="settings" element={<ManagementSettings />} />
            </Route>
            <Route path="/accounting" element={<Protected bare><AccountingLayout /></Protected>}>
              <Route index element={<AccountingDashboard />} />
              <Route path="daily-branch" element={<DailyBranchAccounting />} />
              <Route path="branch-comparison" element={<BranchComparison />} />
              <Route path="settlements" element={<SettlementReconciliation />} />
              <Route path="general-ledger" element={<GeneralLedger />} />
              <Route path="journals" element={<Journals />} />
              <Route path="chart-of-accounts" element={<ChartOfAccounts />} />
              <Route path="accounts-payable" element={<AccountsPayable />} />
              <Route path="accounts-receivable" element={<AccountsReceivable />} />
              <Route path="bank-cash" element={<BankCash />} />
              <Route path="reconciliation" element={<Reconciliation />} />
              <Route path="tax-vat" element={<TaxVAT />} />
              <Route path="budget-actual" element={<BudgetActual />} />
              <Route path="reports" element={<AccReports />} />
              <Route path="reports-hub" element={<ReportsHub />} />
              <Route path="statements" element={<StatementsHub />} />
              <Route path="settings" element={<AccountingSettings />} />
            </Route>
            {ROUTES.map(([path, Comp, bare]) => (
              <Route key={path} path={path} element={<Protected bare={bare}><Comp /></Protected>} />
            ))}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
